import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Video,
  Users,
  MessageSquare,
  Send,
  Heart,
  ThumbsUp,
  PartyPopper,
  Flame,
  ChevronLeft,
  Volume2,
  VolumeX,
  Maximize,
  Play,
  Pause,
  Settings,
  Share2,
  UserPlus,
  Smile,
  Camera,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mock family members watching the game
const MOCK_VIEWERS = [
  { id: 1, name: 'Mom', avatar: '👩', status: 'watching', reaction: null },
  { id: 2, name: 'Dad', avatar: '👨', status: 'watching', reaction: null },
  { id: 3, name: 'Grandma', avatar: '👵', status: 'watching', reaction: null },
  { id: 4, name: 'Grandpa', avatar: '👴', status: 'watching', reaction: null },
  { id: 5, name: 'Uncle Mike', avatar: '👨‍🦱', status: 'watching', reaction: null },
  { id: 6, name: 'Aunt Sarah', avatar: '👩‍🦰', status: 'watching', reaction: null },
  { id: 7, name: 'Cousin Jake', avatar: '🧑', status: 'watching', reaction: null },
  { id: 8, name: 'Coach Williams', avatar: '🧔', status: 'watching', reaction: null },
];

const REACTIONS = [
  { emoji: '🔥', name: 'fire' },
  { emoji: '👏', name: 'clap' },
  { emoji: '💪', name: 'strong' },
  { emoji: '🏀', name: 'basketball' },
  { emoji: '❤️', name: 'heart' },
  { emoji: '😱', name: 'shocked' },
];

const WatchParty = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewers, setViewers] = useState(MOCK_VIEWERS);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const chatEndRef = useRef(null);
  const videoRef = useRef(null);

  // Mock game score state
  const [gameState, setGameState] = useState({
    homeTeam: 'Lakers',
    awayTeam: 'Warriors',
    homeScore: 45,
    awayScore: 42,
    quarter: 2,
    timeRemaining: '5:32',
    possession: 'home',
  });

  const fetchTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams/${teamId}`);
      if (res.ok) {
        const data = await res.json();
        setTeam(data);
        setGameState(prev => ({
          ...prev,
          homeTeam: data.name || 'Home Team',
        }));
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
    setLoading(false);
  }, [teamId]);

  const initializeMockChat = useCallback(() => {
    const initialMessages = [
      { id: 1, user: 'Mom', avatar: '👩', text: 'Go team! 🏀', timestamp: new Date(Date.now() - 300000) },
      { id: 2, user: 'Dad', avatar: '👨', text: 'Great defense!', timestamp: new Date(Date.now() - 240000) },
      { id: 3, user: 'Grandma', avatar: '👵', text: 'Is that our grandson? He looks so tall!', timestamp: new Date(Date.now() - 180000) },
      { id: 4, user: 'Uncle Mike', avatar: '👨‍🦱', text: 'What a play! 🔥', timestamp: new Date(Date.now() - 120000) },
      { id: 5, user: 'Coach Williams', avatar: '🧔', text: 'Nice ball movement team!', timestamp: new Date(Date.now() - 60000) },
    ];
    setMessages(initialMessages);
  }, []);

  const simulateGameUpdate = useCallback(() => {
    setGameState(prev => {
      const scoringTeam = Math.random() > 0.5 ? 'home' : 'away';
      const points = Math.random() > 0.7 ? 3 : 2;
      
      // Update time
      let [mins, secs] = prev.timeRemaining.split(':').map(Number);
      secs -= Math.floor(Math.random() * 30) + 10;
      if (secs < 0) {
        mins -= 1;
        secs += 60;
      }
      if (mins < 0) {
        mins = 8;
        secs = 0;
      }
      
      const newState = {
        ...prev,
        homeScore: scoringTeam === 'home' ? prev.homeScore + points : prev.homeScore,
        awayScore: scoringTeam === 'away' ? prev.awayScore + points : prev.awayScore,
        timeRemaining: `${mins}:${secs.toString().padStart(2, '0')}`,
        possession: scoringTeam === 'home' ? 'away' : 'home',
      };

      // Add chat message for score
      if (Math.random() > 0.5) {
        const viewer = MOCK_VIEWERS[Math.floor(Math.random() * MOCK_VIEWERS.length)];
        const scoreMessages = [
          `${points} points! 🏀`,
          'What a shot!',
          'Yes! Great play!',
          `${scoringTeam === 'home' ? 'Our team' : 'They'} scored!`,
          '🔥🔥🔥',
        ];
        addMessage(viewer, scoreMessages[Math.floor(Math.random() * scoreMessages.length)]);
      }

      return newState;
    });
  }, []);

  const simulateViewerReaction = useCallback(() => {
    const viewerIndex = Math.floor(Math.random() * viewers.length);
    const reaction = REACTIONS[Math.floor(Math.random() * REACTIONS.length)];
    
    setViewers(prev => prev.map((v, i) => 
      i === viewerIndex ? { ...v, reaction: reaction.emoji } : v
    ));

    // Clear reaction after animation
    setTimeout(() => {
      setViewers(prev => prev.map((v, i) => 
        i === viewerIndex ? { ...v, reaction: null } : v
      ));
    }, 2000);

    // Add floating reaction
    addFloatingReaction(reaction.emoji);
  }, [viewers.length]);

  const addFloatingReaction = useCallback((emoji) => {
    const id = Date.now();
    const left = Math.random() * 80 + 10;
    setFloatingReactions(prev => [...prev, { id, emoji, left }]);
    
    setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => r.id !== id));
    }, 3000);
  }, []);

  const addMessage = useCallback((viewer, text) => {
    const newMsg = {
      id: Date.now(),
      user: viewer.name,
      avatar: viewer.avatar,
      text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev.slice(-50), newMsg]);
  }, []);

  // Initialize data and intervals
  useEffect(() => {
    fetchTeam();
    initializeMockChat();
    
    // Simulate live game updates
    const gameInterval = setInterval(simulateGameUpdate, 8000);
    // Simulate viewer reactions
    const reactionInterval = setInterval(simulateViewerReaction, 5000);
    
    return () => {
      clearInterval(gameInterval);
      clearInterval(reactionInterval);
    };
  }, [fetchTeam, initializeMockChat, simulateGameUpdate, simulateViewerReaction]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const myMessage = {
      id: Date.now(),
      user: 'You',
      avatar: '😊',
      text: newMessage,
      timestamp: new Date(),
      isMe: true,
    };
    setMessages(prev => [...prev, myMessage]);
    setNewMessage('');

    // Simulate response
    setTimeout(() => {
      const viewer = MOCK_VIEWERS[Math.floor(Math.random() * MOCK_VIEWERS.length)];
      const responses = [
        'Totally agree! 👍',
        'Yes! 🏀',
        'Let\'s go!',
        '💪💪',
        'Great energy!',
      ];
      addMessage(viewer, responses[Math.floor(Math.random() * responses.length)]);
    }, 2000 + Math.random() * 3000);
  };

  const handleReaction = (emoji) => {
    addFloatingReaction(emoji);
    toast.success(`You reacted with ${emoji}`);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rooam-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rooam-black">
      {/* Header */}
      <div className="bg-gradient-to-r from-rooam-gray-dark to-rooam-gray-medium border-b border-rooam-gray-light">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <PartyPopper className="h-5 w-5 text-yellow-400" />
                  Watch Party
                </h1>
                <p className="text-sm text-gray-400">{team?.name || 'Team'} Game</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-red-500/20 text-red-400 border-red-500 animate-pulse">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                LIVE
              </Badge>
              <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue">
                <Users className="h-3 w-3 mr-1" />
                {viewers.length} watching
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Video Player */}
          <div className="lg:col-span-3 space-y-4">
            {/* Video Container */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light overflow-hidden relative">
              <div className="relative aspect-video bg-black">
                {/* Mock Video Player with Basketball Court */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-900/30 to-orange-700/20">
                  <img 
                    src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1280&h=720&fit=crop"
                    alt="Basketball game"
                    className="w-full h-full object-cover opacity-90"
                  />
                </div>

                {/* Floating Reactions */}
                {floatingReactions.map(reaction => (
                  <div
                    key={reaction.id}
                    className="absolute bottom-20 text-4xl animate-bounce pointer-events-none"
                    style={{ 
                      left: `${reaction.left}%`,
                      animation: 'floatUp 3s ease-out forwards'
                    }}
                  >
                    {reaction.emoji}
                  </div>
                ))}

                {/* Scoreboard Overlay */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-sm rounded-lg px-6 py-3 border border-gray-700">
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-xs text-gray-400 uppercase tracking-wider">{gameState.homeTeam}</p>
                      <p className="text-3xl font-bold text-white">{gameState.homeScore}</p>
                      {gameState.possession === 'home' && (
                        <div className="w-2 h-2 bg-yellow-400 rounded-full mx-auto mt-1"></div>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400">Q{gameState.quarter}</p>
                      <p className="text-xl font-mono text-rooam-blue">{gameState.timeRemaining}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400 uppercase tracking-wider">{gameState.awayTeam}</p>
                      <p className="text-3xl font-bold text-white">{gameState.awayScore}</p>
                      {gameState.possession === 'away' && (
                        <div className="w-2 h-2 bg-yellow-400 rounded-full mx-auto mt-1"></div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsPlaying(!isPlaying)}
                        className="text-white hover:bg-white/20"
                      >
                        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsMuted(!isMuted)}
                        className="text-white hover:bg-white/20"
                      >
                        {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                      >
                        <Settings className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20"
                      >
                        <Maximize className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Live indicator */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-600 text-white border-0 animate-pulse">
                    ● LIVE
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Quick Reactions Bar */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-400">React:</p>
                  <div className="flex gap-2">
                    {REACTIONS.map(reaction => (
                      <Button
                        key={reaction.name}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(reaction.emoji)}
                        className="text-2xl hover:bg-rooam-gray-light hover:scale-125 transition-transform"
                      >
                        {reaction.emoji}
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-rooam-blue text-rooam-blue hover:bg-rooam-blue hover:text-black"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Invite Family
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Viewers Grid */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-rooam-blue" />
                    Family & Friends Watching
                  </h3>
                  <Button variant="ghost" size="sm" className="text-rooam-blue">
                    <UserPlus className="h-4 w-4 mr-1" />
                    Invite
                  </Button>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                  {viewers.map(viewer => (
                    <div key={viewer.id} className="relative text-center">
                      <div className="relative inline-block">
                        <div className="w-12 h-12 rounded-full bg-rooam-gray-dark flex items-center justify-center text-2xl border-2 border-green-500">
                          {viewer.avatar}
                        </div>
                        {viewer.reaction && (
                          <div className="absolute -top-2 -right-2 text-xl animate-bounce">
                            {viewer.reaction}
                          </div>
                        )}
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-rooam-gray-medium"></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 truncate">{viewer.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chat Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light h-[calc(100vh-200px)] flex flex-col">
              <div className="p-3 border-b border-rooam-gray-light">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-rooam-blue" />
                  Watch Party Chat
                </h3>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex gap-2 ${msg.isMe ? 'justify-end' : ''}`}
                  >
                    {!msg.isMe && (
                      <div className="w-8 h-8 rounded-full bg-rooam-gray-dark flex items-center justify-center text-lg flex-shrink-0">
                        {msg.avatar}
                      </div>
                    )}
                    <div className={`max-w-[80%] ${msg.isMe ? 'order-first' : ''}`}>
                      <div className={`rounded-lg px-3 py-2 ${
                        msg.isMe 
                          ? 'bg-rooam-blue text-black' 
                          : 'bg-rooam-gray-dark text-white'
                      }`}>
                        {!msg.isMe && (
                          <p className="text-xs text-rooam-blue font-semibold mb-1">{msg.user}</p>
                        )}
                        <p className="text-sm">{msg.text}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 px-1">
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                    {msg.isMe && (
                      <div className="w-8 h-8 rounded-full bg-rooam-blue flex items-center justify-center text-lg flex-shrink-0">
                        😊
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-3 border-t border-rooam-gray-light">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white text-sm"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="bg-rooam-blue hover:bg-rooam-blue-light text-black"
                    disabled={!newMessage.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style jsx>{`
        @keyframes floatUp {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-200px) scale(1.5);
          }
        }
      `}</style>
    </div>
  );
};

export default WatchParty;
